import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import React from "react";
import { useTranslation } from "react-i18next";
import type { IAnswerErrors } from "./dilemma-form-step-two";

interface IAnswer {
  id: number;
  answer: string;
  feedback: string;
  isCorrect: boolean;
}

interface DilemmaFormAnswersSectionProps {
  formAnswers: IAnswer[];
  setFormAnswers: React.Dispatch<React.SetStateAction<IAnswer[]>>;
  errors: IAnswerErrors;
  disabled?: boolean;
}

const DilemmaFormAnswersSection = ({
  formAnswers,
  setFormAnswers,
  errors,
  disabled,
}: DilemmaFormAnswersSectionProps) => {
  const { t } = useTranslation();

  const toggleAnswer = (id: number) => {
    setFormAnswers((prev) =>
      prev.map((a) => (a.id === id ? { ...a, isCorrect: !a.isCorrect } : a)),
    );
  };
  const handleAnswerChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    id: number,
  ) => {
    setFormAnswers((prev) =>
      prev.map((a) => (a.id === id ? { ...a, answer: e.target.value } : a)),
    );
  };
  const handleFeedbackChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    id: number,
  ) => {
    setFormAnswers((prev) =>
      prev.map((a) => (a.id === id ? { ...a, feedback: e.target.value } : a)),
    );
  };
  return (
    <div className="flex flex-col gap-4">
      {formAnswers.map((answer) => (
        <div key={answer.id} className="flex gap-2 lg:gap-4">
          <Checkbox
            data-testid={`checkbox-${answer.id}`}
            className="size-6 translate-y-16"
            checked={answer.isCorrect}
            onCheckedChange={() => toggleAnswer(answer.id)}
            disabled={disabled}
          />
          <div
            className={cn(
              "flex-1 flex flex-col gap-1",
              answer.isCorrect && "rounded-md border",
            )}
          >
            <div className="px-3 pb-3 flex flex-col gap-2">
              <p
                className={cn(
                  "w-fit px-2 py-1 mt-3 rounded-sm text-gray-600",
                  answer.isCorrect && "bg-green-100",
                )}
              >
                {t("keys.answer")} {answer.id}
              </p>
              <Textarea
                data-testid={`answer-${answer.id}`}
                className={cn(
                  "min-h-20",
                  answer.isCorrect && "border-green-500",
                  errors[answer.id]?.answer && "border-destructive",
                )}
                value={answer.answer}
                onChange={(e) => handleAnswerChange(e, answer.id)}
                disabled={disabled}
              />
              {errors[answer.id]?.answer && (
                <p className="text-sm text-destructive">
                  {errors[answer.id].answer}
                </p>
              )}
            </div>
            <>
              <hr />
              <div className="p-3 flex flex-col gap-2">
                <span className="text-gray-600">
                  {t("keys.associated_feedback")}
                </span>
                <Textarea
                  data-testid={`feedback-${answer.id}`}
                  className={cn(
                    "min-h-20",
                    errors[answer.id]?.feedback && "border-destructive",
                  )}
                  value={answer.feedback}
                  onChange={(e) => handleFeedbackChange(e, answer.id)}
                  disabled={disabled}
                />
                {errors[answer.id]?.feedback && (
                  <p className="text-sm text-destructive">
                    {errors[answer.id].feedback}
                  </p>
                )}
              </div>
            </>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DilemmaFormAnswersSection;
